import { ButtonProps } from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { Dialog } from "../../../components/Dialog";
import {
  UpdateInstanceWhatsAppData,
  WhatsAppInstance,
} from "../../../types/whatsApp";
import { useStyles } from "../styles";
import { useMutation, useQuery } from "react-query";
import { listStores } from "../../../services/insights";
import { Autocomplete } from "../../../components/Autocomplete";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
} from "@mui/material";
import { useToast } from "../../../hooks/useToast";
import { updateInstance } from "../../../services/whatsApp";
import { queryClient } from "../../../services/queryClient";

type ConfigWhatsAppProps = {
  data: WhatsAppInstance | null;
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({});

export function ConfigWhatsApp({ data, open, onClose }: ConfigWhatsAppProps) {
  const styles = useStyles();
  const toast = useToast();

  const putInstance = useMutation(
    async (data: UpdateInstanceWhatsAppData) => {
      const response = await updateInstance(data);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("whatsAppInstances");
        toast.show("Instância atualizada com sucesso!", {
          severity: "success",
        });
        onClose();
      },
      onError: () => {
        toast.show("Erro ao atualizar a instância!", { severity: "error" });
      },
    }
  );

  const handleSave = useCallback(
    async (data: UpdateInstanceWhatsAppData) => {
      await putInstance.mutateAsync(data);
    },
    [putInstance]
  );

  const formik = useFormik<UpdateInstanceWhatsAppData>({
    initialValues: {} as UpdateInstanceWhatsAppData,
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSave,
  });

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Fechar",
        onClick: onClose,
      },
      {
        disabled: formik.isSubmitting,
        variant: "contained",
        children: formik.isSubmitting ? "Enviando solicitação" : "Editar",
        onClick: () => formik.handleSubmit(),
      },
    ];
  }, [onClose, formik]);

  const stores = useQuery("stores", listStores);

  useEffect(() => {
    if (data?.id) {
      const dataStore = stores.data?.find(
        (store) => store.id_loja === data?.id_loja
      );
      formik.setFieldValue("store", dataStore);
      formik.setFieldValue("id", data.id);
      formik.setFieldValue("instance_type", data.tipo_instancia);
      formik.setFieldValue("name", data.name);
      formik.setFieldValue("own_number", data.own_number);
      formik.setFieldValue(
        "automatically_activate_contacts",
        data.automatically_activate_contacts
      );
      formik.setFieldValue(
        "automatically_save_groups",
        data.automatically_save_groups
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, stores.data]);

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title={data?.name}
      open={open}
      actions={actions}
    >
      <Stack spacing={4} className={styles.rootContent}>
        {formik.values.instance_type ? (
          <RadioGroup
            row
            value={formik.values.instance_type}
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            onChange={(_: any, value: string) => {
              formik.setFieldValue("instance_type", value);
            }}
          >
            <FormControlLabel value={1} control={<Radio />} label="Disparo" />
            <FormControlLabel
              value={2}
              control={<Radio />}
              label="Disparo + Atendimento"
            />
          </RadioGroup>
        ) : (
          <Skeleton variant="text" />
        )}
        <TextField
          label="Nome"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("name", value)
          }
          fullWidth
          size="small"
          value={formik.values.name}
        />
        {formik.values.store ? (
          <Autocomplete
            size="small"
            label="Lojas"
            loading={stores.isLoading}
            options={stores.data}
            getOptionLabel={(option) => option.nome_loja}
            value={formik.values.store}
            onChange={(_, value) => {
              formik.setFieldValue("store", value);
            }}
          />
        ) : (
          <Skeleton variant="text" />
        )}
        <TextField
          type="tel"
          label="Número do celular"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("own_number", value)
          }
          fullWidth
          size="small"
          value={formik.values.own_number}
        />
        {typeof formik.values.automatically_save_groups === "number" ? (
          <FormControlLabel
            checked={Boolean(formik.values.automatically_activate_contacts)}
            onChange={(_, checked) => {
              formik.setFieldValue(
                "automatically_activate_contacts",
                checked ? 1 : 0
              );
            }}
            control={<Checkbox />}
            label="Ativar contatos automaticamente"
          />
        ) : (
          <Skeleton variant="text" />
        )}
        {typeof formik.values.automatically_save_groups === "number" ? (
          <FormControlLabel
            checked={Boolean(formik.values.automatically_save_groups)}
            onChange={(_, checked) => {
              formik.setFieldValue(
                "automatically_save_groups",
                checked ? 1 : 0
              );
            }}
            control={<Checkbox />}
            label="Ativar grupos automaticamente"
          />
        ) : (
          <Skeleton variant="text" />
        )}
        <Alert severity="info" variant="filled">
          As configurações serão aplicadas quando o whatsapp for conectado
          novamente.
        </Alert>
      </Stack>
    </Dialog>
  );
}
